/**
 * What a failed request leaves for the Error Log beyond its own message: the
 * id that ties it to the server's log lines, why it never connected, and what
 * answered when the platform did not. Shared by apiFetch and adminFetch.
 */

/** A request id the server accepts as-is: its middleware keeps [A-Za-z0-9_-.]{1,128}. */
export function newRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** The reply read once: parsed when it is JSON, and its text either way. */
export async function readReply(res: Response): Promise<{ json: unknown; text: string }> {
  const text = await res.text().catch(() => '');
  if (!text) return { json: null, text };
  try {
    return { json: JSON.parse(text) as unknown, text };
  } catch {
    return { json: null, text };
  }
}

const BODY_CHARS = 300;

/** What answered instead of the platform: status, content type, server, and the start of the body. */
export function foreignReply(res: Response, text: string): string {
  const type = res.headers.get('Content-Type') ?? 'no content type';
  // Readable only same-origin or when exposed; left out when the browser hides it.
  const server = res.headers.get('Server');
  const body = text.replace(/\s+/g, ' ').trim().slice(0, BODY_CHARS);
  return `Reply was not the platform's: HTTP ${res.status}, ${type}${server ? `, server ${server}` : ''}, ${body ? `body "${body}"` : 'empty body'}`;
}

/** Why a request never connected, as far as the browser can say. */
export function unreachableDetail(err: Error, startedAt: number): string {
  const seconds = ((Date.now() - startedAt) / 1000).toFixed(1);
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return `${err.message || 'no reason given'} - after ${seconds}s, browser reported itself ${offline ? 'offline' : 'online'}`;
}
